import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { GeolocationLayoutProps } from "@companion-core/shared/app/Interfaces/blocks";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { getSanitizedDescriptionAlt } from "@companion-core/shared/app/Utils/componentHelper";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import "Assets/Styles/scss/geolocationLayout.scss";
import { useHeaderStore } from "@companion-core/shared/app/Store/header";
import { useOnboardingStore } from "@companion-core/shared/app/Store/onboardingStore";
import { useTranslation } from "react-i18next";
import {
  trackGeolocationAuthorizationClick,
  trackGeolocationAuthorizationPage,
} from "@companion-core/shared/app/tracking/blocks";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";

type Coordinates = {
  latitude: number;
  longitude: number;
};

export const GeolocationLayout = ({
  mediaUrl,
  iconUrl,
  iconDescriptionAlt,
  title,
  description,
}: GeolocationLayoutProps) => {
  const [browserPermissionState, setBrowserPermissionState] = useState<
    "granted" | "denied" | "prompt" | "unknown"
  >("unknown");
  const { setDisabledHeader } = useHeaderStore((state) => ({
    setDisabledHeader: state.setDisabledHeader,
  }));
  const {
    geolocationAlreadyRequested,
    geolocationGranted,
    universe,
    setGeolocationAlreadyRequested,
    setGeolocationGranted,
  } = useOnboardingStore((state) => ({
    geolocationAlreadyRequested: state.geolocationAlreadyRequested,
    geolocationGranted: state.geolocationGranted,
    universe: state.universe,
    setGeolocationAlreadyRequested: state.setGeolocationAlreadyRequested,
    setGeolocationGranted: state.setGeolocationGranted,
  }));
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const backgroundImage = useMemo(() => {
    return mediaUrl
      ? `url(${getCloudinaryOptimizedUrl(mediaUrl, {
          contentType: "image",
          width: 1920,
        })})`
      : undefined;
  }, [mediaUrl]);

  const iconSrc = useMemo(() => {
    return iconUrl
      ? getCloudinaryOptimizedUrl(iconUrl, {
          contentType: "logo",
        })
      : undefined;
  }, [iconUrl]);

  const redirectToRecommendedList = useCallback(
    (coordinates?: Coordinates) => {
      if (!coordinates) {
        navigate("/recommended-list");
        return;
      }
      const searchParams = new URLSearchParams({
        latitude: String(coordinates.latitude),
        longitude: String(coordinates.longitude),
      });
      navigate(`/recommended-list?${searchParams.toString()}`);
    },
    [navigate]
  );

  const redirectToRecommendedListLater = useCallback(() => {
    trackGeolocationAuthorizationClick({
      universeSlug: universe?.slug || "",
      status: "later",
    });
    redirectToRecommendedList();
  }, [redirectToRecommendedList, universe?.slug]);

  const getGeolocationPermission = useCallback(async () => {
    if (!navigator.permissions?.query) return "prompt";

    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });
      return permission.state;
    } catch {
      return "prompt";
    }
  }, []);

  const requestGeolocationAccess = useCallback(async () => {
    trackGeolocationAuthorizationClick({
      universeSlug: universe?.slug || "",
      status: "OK",
    });
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeolocationAlreadyRequested(true);
        setGeolocationGranted(true);
        redirectToRecommendedList({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      async (error) => {
        setGeolocationAlreadyRequested(true);

        const permission = await getGeolocationPermission();
        setBrowserPermissionState(permission);
        if (error.code === error.PERMISSION_DENIED && permission === "denied") {
          setGeolocationGranted(false);
          redirectToRecommendedList();
          return;
        }

        if (permission === "granted") {
          setGeolocationGranted(true);
        }
      }
    );
  }, [
    geolocationGranted,
    getGeolocationPermission,
    setGeolocationAlreadyRequested,
    setGeolocationGranted,
    redirectToRecommendedList,
  ]);

  useMountEffect(() => {
    trackGeolocationAuthorizationPage({
      universeSlug: universe?.slug || "",
    });
  });

  useMountEffect(() => {
    setDisabledHeader(true);
    return () => setDisabledHeader(false);
  });

  useEffect(() => {
    if (!geolocationAlreadyRequested) return;
    getGeolocationPermission()
      .then((permission) => {
        setBrowserPermissionState(permission);
        if (permission === "granted") {
          setGeolocationGranted(true);
          requestGeolocationAccess();
          return;
        }
        if (permission === "denied" && !geolocationGranted) {
          setGeolocationGranted(false);
        }
      })
      .catch(() => {
        setBrowserPermissionState("prompt");
      });
  }, [
    geolocationAlreadyRequested,
    geolocationGranted,
    getGeolocationPermission,
    requestGeolocationAccess,
    setGeolocationGranted,
  ]);

  if (geolocationAlreadyRequested && browserPermissionState === "denied" && !geolocationGranted) {
    return <Navigate to="/recommended-list" replace />;
  }

  return (
    <div
      className="geolocation-layout"
      data-testid="geolocation-layout"
      style={{ backgroundImage: isMobileSize ? backgroundImage : undefined }}>
      {!isMobileSize && (
        <section
          className="geolocation-layout__media"
          data-testid="geolocation-layout__image--background-image"
          style={{ backgroundImage }}>
          <button
            className="geolocation-layout__back-button"
            data-testid="geolocation-layout__button--back"
            type="button"
            onClick={() => navigate(-1)}>
            <FontIcon
              name="JP-Back"
              size={20}
              className="geolocation-layout__back-button-icon"
              aria-hidden="true"
            />
            <span className="body-text-6">{t("geolocation.buttons.back")}</span>
          </button>
        </section>
      )}
      <section className="geolocation-layout__content">
        <div className="geolocation-layout__content-inner">
          {isMobileSize && (
            <button
              className="geolocation-layout__back-button geolocation-layout__back-button--mobile"
              data-testid="geolocation-layout__button--back"
              type="button"
              onClick={() => navigate(-1)}>
              <FontIcon
                name="JP-Arrow-left-bold"
                size={24}
                className="geolocation-layout__back-button-icon"
                aria-hidden="true"
              />
              <span className="body-text-6">{t("geolocation.buttons.back")}</span>
            </button>
          )}
          {iconSrc ? (
            <img
              src={iconSrc}
              className="geolocation-layout__icon"
              alt={getSanitizedDescriptionAlt(iconDescriptionAlt) ?? ""}
              data-testid="geolocation-layout__icon"
            />
          ) : (
            <FontIcon
              name="JP-Location"
              size={20}
              className="geolocation-layout__icon geolocation-layout__icon--fallback"
              aria-hidden="true"
              data-testid="geolocation-layout__icon"
            />
          )}
          {title && (
            <h1
              className="geolocation-layout__title title-1"
              data-testid="geolocation-layout__title">
              {title}
            </h1>
          )}
          {description && (
            <div
              className="geolocation-layout__description body-text-6"
              data-testid="geolocation-layout__description"
              // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            />
          )}
          <div className="geolocation-layout__actions">
            <Button
              testID="geolocation-layout__button--allow"
              type={isMobileSize ? "secondary" : "primary"}
              negative={isMobileSize}
              size="full"
              label={t("geolocation.buttons.allow")}
              classNames={["geolocation-layout__allow-button", "body-text-4"]}
              onClick={requestGeolocationAccess}
            />
            <Button
              testID="geolocation-layout__button--later"
              type="link"
              negative={false}
              label={t("geolocation.buttons.later")}
              classNames={["geolocation-layout__later-button", "body-text-4"]}
              onClick={redirectToRecommendedListLater}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
